import EventEmitter from "events";
import { BookingStatus, Currency } from "@finbackoffice/enums";
import { HoursRange } from "../common";
import {
  CLIENT_CALENDAR,
  CLIENT_CALENDAR_TREE,
  CLIENT_CHAT_MESSAGE,
  CLIENT_CONNECTED,
  CLIENT_DEPOSIT,
  CLIENT_DEPOSIT_BATCH,
  CLIENT_DISCONNECTED,
  CLIENT_EXCHANGE_RATES_LATEST,
  CLIENT_EXCHANGE_RATES_TIME,
  CLIENT_FEED_LIVE,
  CLIENT_FEED_PREMATCH,
  CLIENT_PING,
  CLIENT_SUBSCRIBE_EVENT,
  CLIENT_SUBSCRIBE_MARKET,
  CLIENT_SUBSCRIBE_TOURNAMENT,
  CLIENT_TICKET,
  CLIENT_TOP_GAMES,
  CLIENT_UPCOMING_GAMES,
  CLIENT_WALLET,
  CLIENT_WITHDRAWAL,
  CLIENT_WITHDRAWAL_BATCH,
} from "../constants/events";
import { IEventFeedResponse } from "../utils";

type IPingResponse = {
  time: string;
};

export type IClientError =
  | {
      error: string;
      message: string;
      statusCode: number;
    }
  | undefined;

type ParamsMandatory = true;
type ParamsOptional = false;

type IClientEventsMap<
  TWithdrawalResponse = any,
  TDepositResponse = any,
  TTicketResponse = any,
  TChatMessageResponse = any,
  TFeedConfigResponse = any,
  TEventFeedResponse = any,
  TExchangeRateBatchResponse = any,
  TMarketFeedBetSlip = any,
  TWalletResponse = any,
  TExchangeRatesMapResponse = any,
> = {
  [CLIENT_WITHDRAWAL]: [TWithdrawalResponse, IClientError, { token: string }, ParamsMandatory];
  [CLIENT_WITHDRAWAL_BATCH]: [TWithdrawalResponse[], IClientError, { token: string }, ParamsMandatory];
  [CLIENT_DEPOSIT]: [TDepositResponse, IClientError, { token: string }, ParamsMandatory];
  [CLIENT_DEPOSIT_BATCH]: [TDepositResponse[], IClientError, { token: string }, ParamsMandatory];
  [CLIENT_TICKET]: [
    TTicketResponse,
    IClientError,
    {
      token: string;
      localized?: boolean;
    },
    ParamsMandatory,
  ];
  [CLIENT_FEED_PREMATCH]: [
    { rid: string },
    IClientError,
    {
      hours: HoursRange;
      booking_status?: BookingStatus;
    },
    ParamsOptional,
  ];
  [CLIENT_FEED_LIVE]: [{ rid: string }, IClientError, never];
  [CLIENT_SUBSCRIBE_EVENT]: [
    TEventFeedResponse,
    IClientError,
    {
      event_id: number;
      token?: string;
    },
    ParamsMandatory,
  ];
  [CLIENT_SUBSCRIBE_TOURNAMENT]: [
    TEventFeedResponse[],
    IClientError,
    {
      tournament_id: number;
      from?: number;
      to?: number;
      booking_status?: BookingStatus;
    },
    ParamsMandatory,
  ];
  [CLIENT_SUBSCRIBE_MARKET]: [
    TMarketFeedBetSlip,
    IClientError,
    {
      event_id: number;
      market_id: number;
      outcome_id: number;
      wallet_id?: number;
    },
    ParamsMandatory,
  ];
  [CLIENT_CALENDAR]: [
    IEventFeedResponse[],
    IClientError,
    {
      from: number;
      to: number;
      tournament_id?: number[];
    },
    ParamsOptional,
  ];
  [CLIENT_CALENDAR_TREE]: [
    { rid: string },
    IClientError,
    {
      from: number;
      to: number;
    },
    ParamsOptional,
  ];
  [CLIENT_WALLET]: [TWalletResponse, IClientError, { token: string }, ParamsMandatory];
  [CLIENT_CHAT_MESSAGE]: [TChatMessageResponse, IClientError, { token: string }, ParamsMandatory];
  [CLIENT_CONNECTED]: [TFeedConfigResponse, IClientError, never];
  [CLIENT_EXCHANGE_RATES_LATEST]: [
    TExchangeRatesMapResponse,
    IClientError,
    {
      fromCurrency: Currency[];
      toCurrencies: Currency[];
    },
    ParamsMandatory,
  ];
  [CLIENT_EXCHANGE_RATES_TIME]: [
    TExchangeRateBatchResponse,
    IClientError,
    {
      time: string;
      fromCurrency: Currency;
      toCurrencies: Currency[];
    },
    ParamsMandatory,
  ];
  [CLIENT_TOP_GAMES]: [
    IEventFeedResponse[],
    IClientError,
    {
      hours: HoursRange;
    },
  ];
  [CLIENT_UPCOMING_GAMES]: [
    IEventFeedResponse[],
    IClientError,
    {
      hours: HoursRange;
    },
  ];
  [CLIENT_PING]: [IPingResponse, IClientError, never];
  [CLIENT_DISCONNECTED]: [any, IClientError, never];
};

export type IMessagesToClient = keyof IClientEventsMap;

export type ClientEventHandler<T extends IMessagesToClient> = (...args: IClientEventsMap[T]) => void;

export type ClientSubscriptionParams<T extends IMessagesToClient> = IClientEventsMap[T][2];

// This generic allows to make ...rest parameters optional in case
export type ISubscriptionPayloadParams<T extends IMessagesToClient> = IClientEventsMap[T][2] extends never
  ? []
  : IClientEventsMap[T][3] extends ParamsMandatory
    ? [IClientEventsMap[T][2]]
    : [IClientEventsMap[T][2]?];

export class ClientsService {
  private readonly eventEmitter: EventEmitter = new EventEmitter();

  public subscribe<T extends IMessagesToClient>(rid: string, handler: ClientEventHandler<T>) {
    this.eventEmitter.on(rid, handler as any);
  }

  public unsubscribe<T extends IMessagesToClient>(rid: string, handler: ClientEventHandler<T>) {
    this.eventEmitter.off(rid, handler as any);
  }

  public sendClientMessage<T extends IMessagesToClient>(
    rid: string,
    data: IClientEventsMap[T][0],
    error?: IClientError,
  ): void {
    if (error) {
      this.eventEmitter.emit(rid, undefined, error);
    } else {
      this.eventEmitter.emit(rid, data);
    }
  }

  public sendClientMessageByHandler<T extends IMessagesToClient>(
    rid: string,
    data: IClientEventsMap[T][0],
    handler: ClientEventHandler<T>,
  ): void {
    const listeners = this.eventEmitter.listeners(rid);
    const listener = listeners.find((l) => l === handler);
    if (listener) {
      listener(data);
    }
  }

  public removeAllListeners() {
    this.eventEmitter.eventNames().forEach((eventName) => {
      // remove all listeners for all events except system events
      if ([CLIENT_CONNECTED, CLIENT_DISCONNECTED].indexOf(eventName as string) === -1) {
        this.eventEmitter.removeAllListeners(eventName);
      }
    });
  }

  public hasListeners(rid: string): boolean {
    const listeners = this.eventEmitter.listeners(rid);
    return listeners.length > 0;
  }
}
